import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { useSelector } from "react-redux";
import { Download, ScrollText } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import DataTable from "../../components/dashboard/DataTable";
import ActivitySummaryCharts from "../../components/dashboard/ActivitySummaryCharts";
import { ActivityIcon, formatActivityRelative } from "../../components/dashboard/RecentActivity";
import Button from "../../components/ui/Button";
import { USE_MOCK_AUTH } from "../../config/api";
import {
  fetchOpsActivityLogs,
  fetchOpsActivityLogsSummary,
  fetchOpsAuditFilters,
} from "../../services/activityLogsApi";
import { activityActorName, resolveActivityPath } from "../../utils/activityPath";
import { exportReport } from "../../utils/exportReport";
import { showError, showSuccess } from "../../utils/toast";
import useRole from "../../hooks/useRole";

const EMPTY_FILTERS = {
  search: "",
  provider_id: "",
  actor_slug: "",
  actor_type: "",
  event_type: "",
  module: "",
  portal: "",
  date_from: "",
  date_to: "",
};

function formatTimestamp(iso) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function portalLabel(item) {
  if (item?.portal === "provider" || item?.audience === "provider") return "Institution";
  if (item?.portal === "system" || item?.audience === "both") return "System";
  return "Ops";
}

function Select({ label, value, onChange, options, emptyLabel }) {
  return (
    <label className="block min-w-[10rem] flex-1">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-primary/10 bg-white px-2.5 py-2 text-xs font-medium text-primary"
      >
        <option value="">{emptyLabel}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function AuditTrailPage() {
  const { isSuperAdmin, isExecutiveManager, isCallCenterManager } = useRole();
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState({
    institutions: [],
    actors: [],
    event_types: [],
    modules: [],
    portals: [],
    actor_types: [],
  });
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(useLiveApi);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exportFormat, setExportFormat] = useState("Excel");

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const loadFilters = useCallback(async () => {
    if (!useLiveApi) return;
    try {
      const data = await fetchOpsAuditFilters(token);
      setOptions({
        institutions: data.institutions || [],
        actors: data.actors || [],
        event_types: data.event_types || [],
        modules: data.modules || [],
        portals: data.portals || [],
        actor_types: data.actor_types || [],
      });
    } catch {
      setOptions({
        institutions: [],
        actors: [],
        event_types: [],
        modules: [],
        portals: [],
        actor_types: [],
      });
    }
  }, [token, useLiveApi]);

  const loadSummary = useCallback(async () => {
    if (!useLiveApi) {
      setSummary(null);
      setSummaryLoading(false);
      return;
    }
    setSummaryLoading(true);
    try {
      setSummary(await fetchOpsActivityLogsSummary(token, { provider_id: applied.provider_id }));
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [applied.provider_id, token, useLiveApi]);

  const load = useCallback(
    async (nextPage = 1) => {
      if (!useLiveApi) {
        setItems([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await fetchOpsActivityLogs(token, { ...applied, page: nextPage, limit: 25 });
        const nextItems = Array.isArray(data?.entries) ? data.entries : [];
        setItems(nextItems);
        setPage(Number(data?.pagination?.current_page || nextPage));
        setLastPage(Number(data?.pagination?.last_page || 1));
        setTotal(Number(data?.pagination?.total || 0));
      } catch (error) {
        showError(error.message || "Could not load audit trail.");
      } finally {
        setLoading(false);
      }
    },
    [applied, token, useLiveApi]
  );

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    load(1);
    loadSummary();
  }, [load, loadSummary]);

  const applyFilters = (event) => {
    event?.preventDefault?.();
    setApplied({ ...filters });
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  };

  const activeFilterCount = useMemo(
    () => Object.values(applied).filter((value) => value !== "").length,
    [applied]
  );

  const handleExport = () => {
    exportReport(
      exportFormat,
      {
        title: "Ops audit trail",
        type: "Audit Trail",
        period: applied.date_from || applied.date_to
          ? `${applied.date_from || "…"} to ${applied.date_to || "…"}`
          : "Filtered page",
        summary,
        entries: items.map((item) => ({
          timestamp: item.created_at,
          actor: activityActorName(item),
          actor_type: item.actor_type,
          portal: portalLabel(item),
          institution: item.institution_name || "",
          event: item.title,
          event_type: item.event_type,
          module: item.module_label || item.module,
          message: item.message,
          ip: item.ip || item.data?.ip || "",
        })),
      },
      "ops-audit-trail"
    );
    showSuccess(`Exported as ${exportFormat}.`);
  };

  return (
    <PageShell
      title="Audit Trail"
      description={
        isSuperAdmin || isExecutiveManager
          ? "Immutable record of every sign-in, change, and module action across ops and institution portals."
          : isCallCenterManager
            ? "Your actions, and activity on the institutions assigned to you."
            : "Your own sign-ins, changes, and module actions."
      }
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            className="rounded-lg border border-primary/10 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary"
          >
            {["Excel", "CSV", "Text", "PDF"].map((fmt) => (
              <option key={fmt} value={fmt}>
                {fmt}
              </option>
            ))}
          </select>
          <Link
            to="/dashboard/sms-logs"
            className="rounded-lg border border-primary/10 bg-white px-2.5 py-1.5 text-xs font-semibold text-primary"
          >
            SMS log
          </Link>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={loading && !items.length}>
            <Download className="h-3.5 w-3.5" />
            Export page
          </Button>
        </div>
      }
    >
      <form
        onSubmit={applyFilters}
        className="mb-4 rounded-xl border border-primary/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(127,29,29,0.04)]"
      >
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="block md:col-span-2">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
              Search
            </span>
            <input
              type="search"
              value={filters.search}
              onChange={(e) => setFilter("search", e.target.value)}
              placeholder="Actor, event, message, institution…"
              className="w-full rounded-lg border border-primary/10 bg-white px-3 py-2 text-xs text-primary"
            />
          </label>
          <Select
            label="Institution"
            value={filters.provider_id}
            onChange={(value) => setFilter("provider_id", value)}
            options={options.institutions}
            emptyLabel="All institutions"
          />
          <Select
            label="User"
            value={filters.actor_slug}
            onChange={(value) => setFilter("actor_slug", value)}
            options={options.actors}
            emptyLabel="All users"
          />
          <Select
            label="User type"
            value={filters.actor_type}
            onChange={(value) => setFilter("actor_type", value)}
            options={options.actor_types}
            emptyLabel="All types"
          />
          <Select
            label="Portal"
            value={filters.portal}
            onChange={(value) => setFilter("portal", value)}
            options={options.portals}
            emptyLabel="All portals"
          />
          <Select
            label="Module"
            value={filters.module}
            onChange={(value) => setFilter("module", value)}
            options={options.modules}
            emptyLabel="All modules"
          />
          <Select
            label="Event"
            value={filters.event_type}
            onChange={(value) => setFilter("event_type", value)}
            options={options.event_types}
            emptyLabel="All events"
          />
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
              From
            </span>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilter("date_from", e.target.value)}
              className="w-full rounded-lg border border-primary/10 bg-white px-2.5 py-2 text-xs text-primary"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.08em] text-primary/40">
              To
            </span>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilter("date_to", e.target.value)}
              className="w-full rounded-lg border border-primary/10 bg-white px-2.5 py-2 text-xs text-primary"
            />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm">
            Apply filters
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={resetFilters}>
            Reset
          </Button>
          {activeFilterCount > 0 ? (
            <span className="text-[11px] font-semibold text-primary/45">
              {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} applied
            </span>
          ) : null}
        </div>
      </form>

      <ActivitySummaryCharts summary={summary} loading={summaryLoading} />

      <div className="mb-3 flex flex-wrap items-center gap-3 text-sm text-primary/50">
        <span className="inline-flex items-center gap-2">
          <ScrollText className="h-4 w-4" />
          <span>{loading ? "Loading…" : `${total.toLocaleString()} recorded action${total === 1 ? "" : "s"}`}</span>
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-primary/[0.07] bg-white">
        {loading && items.length === 0 ? (
          <p className="px-5 py-16 text-center text-sm text-primary/45">Loading audit trail…</p>
        ) : items.length === 0 ? (
          <p className="px-5 py-16 text-center text-sm text-primary/45">No audit events match these filters.</p>
        ) : (
          <DataTable
            embedded
            paginate={false}
            compact
            data={items}
            getRowId={(item) => item.activity_slug || item.id}
            columns={[
              {
                key: "when",
                header: "When",
                render: (item) => (
                  <div>
                    <p className="text-xs font-semibold tabular-nums text-primary">{formatTimestamp(item.created_at)}</p>
                    <p className="text-[10px] text-primary/35">{formatActivityRelative(item.created_at)}</p>
                  </div>
                ),
              },
              {
                key: "actor",
                header: "Actor",
                type: "name",
                render: (item) => (
                  <div>
                    <p className="text-xs font-semibold text-primary">{activityActorName(item)}</p>
                    <p className="text-[10px] capitalize text-primary/40">
                      {item.actor_type === "admin" ? "Ops user" : item.actor_type === "provider" ? "Institution user" : "System"}
                    </p>
                  </div>
                ),
              },
              {
                key: "event",
                header: "Event",
                render: (item) => (
                  <div className="flex items-start gap-2">
                    <ActivityIcon icon={item.icon} color={item.color} className="h-8 w-8" />
                    <div className="min-w-0">
                      <p className="truncate text-xs font-bold text-primary">{item.title}</p>
                      <p className="text-[10px] text-primary/40">{item.module_label || item.module}</p>
                    </div>
                  </div>
                ),
              },
              {
                key: "portal",
                header: "Portal",
                render: (item) => (
                  <span className="rounded-full bg-primary/[0.05] px-2 py-0.5 text-[10px] font-semibold text-primary/70">
                    {portalLabel(item)}
                  </span>
                ),
              },
              {
                key: "institution",
                header: "Institution",
                render: (item) => <span className="text-xs text-primary/70">{item.institution_name || "—"}</span>,
              },
              {
                key: "details",
                header: "Details",
                render: (item) => {
                  const path = resolveActivityPath(item, { portal: "ops" });
                  return (
                    <div>
                      <p className="text-xs leading-relaxed text-primary/65">{item.message}</p>
                      {item.ip || item.data?.ip ? (
                        <p className="mt-1 font-mono text-[10px] text-primary/35">IP {item.ip || item.data.ip}</p>
                      ) : null}
                      {path ? (
                        <Link to={path} className="mt-1.5 inline-flex text-[11px] font-semibold text-collect hover:underline">
                          Open related record
                        </Link>
                      ) : null}
                    </div>
                  );
                },
              },
            ]}
          />
        )}
      </div>

      {lastPage > 1 ? (
        <div className="mt-4 flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => load(page - 1)}>
            Previous
          </Button>
          <span className="text-xs text-primary/45">
            Page {page} of {lastPage}
          </span>
          <Button variant="outline" size="sm" disabled={page >= lastPage || loading} onClick={() => load(page + 1)}>
            Next
          </Button>
        </div>
      ) : null}
    </PageShell>
  );
}
